// src/odoo/customers/components/CustomerProductRecommendations.tsx
// All comments in English.

import React, { useState } from "react";
import { Info, TrendingUp } from "lucide-react";
import { useCustomerProductRecommendations } from "../hooks/useProductInsights";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const formatPercent = (value: number | null | undefined): string =>
  value == null ? "-" : `${(value * 100).toFixed(1)}%`;

interface Props {
  companyPartnerId?: number;
}

// Build human-readable reason based on metrics and trigger products,
// without repeating the (possibly very long) product name.
const buildRecommendationReason = (r: {
  product_name?: string | null;
  co_count?: number | null;
  confidence?: number | null;
  lift?: number | null;
  support?: number | null;
  trigger_product_names?: string[] | null;
}): string => {
  // Always use a general term instead of the full product name.
  const name = "This product";

  const co = r.co_count ?? 0;
  const conf = r.confidence ?? 0;
  const lift = r.lift ?? 0;
  const support = r.support ?? 0;
  const confPct = conf * 100;
  const supportPct = support * 100;

  const triggerNames = (r.trigger_product_names ?? []).filter(
    (x) => !!x && x.trim().length > 0
  );
  const sampleTriggers = triggerNames.slice(0, 3);

  const parts: string[] = [];

  // First clause: relationship between this product and the customer's existing items
  if (co >= 1) {
    if (sampleTriggers.length > 0) {
      const examples = sampleTriggers.join(", ");
      parts.push(
        `${name} is sometimes ordered together with products this customer already buys, such as ${examples}`
      );
    } else {
      parts.push(
        `${name} is sometimes ordered together with products this customer already buys`
      );
    }
  } else {
    parts.push(
      `${name} is recommended based on this customer's historical order patterns`
    );
  }

  // Support clause (overall share of orders)
  if (supportPct >= 5) {
    parts.push(
      `appears together in a meaningful share of orders (${supportPct.toFixed(
        1
      )}%)`
    );
  } else if (supportPct >= 1) {
    parts.push(
      `appears together in a focused segment of orders (${supportPct.toFixed(
        1
      )}%)`
    );
  }

  // Follow-on rate (confidence) clause
  if (confPct >= 50) {
    parts.push(`high follow-on rate (${confPct.toFixed(0)}%)`);
  } else if (confPct >= 20) {
    parts.push(`moderate follow-on rate (${confPct.toFixed(0)}%)`);
  } else if (confPct >= 5) {
    parts.push(`emerging follow-on pattern (${confPct.toFixed(0)}%)`);
  }

  // Lift clause
  if (lift >= 20) {
    parts.push(
      `very strong uplift vs. average demand (lift ${lift.toFixed(1)}x)`
    );
  } else if (lift >= 8) {
    parts.push(`strong uplift vs. average demand (lift ${lift.toFixed(1)}x)`);
  } else if (lift >= 4) {
    parts.push(`clear uplift vs. average demand (lift ${lift.toFixed(1)}x)`);
  } else if (lift >= 2) {
    parts.push(`above-average pairing (lift ${lift.toFixed(1)}x)`);
  } else if (lift >= 1.0) {
    parts.push(
      `slightly more likely to be bought together than average (lift ${lift.toFixed(
        1
      )}x)`
    );
  }

  // Fallback if we somehow have no metric-based parts
  if (parts.length === 0) {
    return `${name} is recommended based on historical order patterns in our company.`;
  }

  // Join clauses into a single sentence-like string
  return parts.join(" • ");
};

// Helper to categorize lift into strength buckets for UI.
const getLiftCategory = (lift: number | null | undefined): {
  label: string;
  className: string;
} => {
  if (lift == null) {
    return {
      label: "No lift data",
      className: "bg-slate-100 text-slate-600 border-slate-200",
    };
  }

  if (lift >= 50) {
    return {
      label: "Very strong",
      className: "bg-emerald-100 text-emerald-800 border-emerald-200",
    };
  }
  if (lift >= 8) {
    return {
      label: "Strong",
      className: "bg-green-100 text-green-800 border-green-200",
    };
  }
  if (lift >= 4) {
    return {
      label: "Moderate",
      className: "bg-amber-100 text-amber-800 border-amber-200",
    };
  }
  if (lift >= 1) {
    return {
      label: "Weak",
      className: "bg-slate-100 text-slate-700 border-slate-200",
    };
  }
  return {
    label: "Negative",
    className: "bg-rose-100 text-rose-800 border-rose-200",
  };
};

// Derive small "pattern tags" from existing metrics only.
const buildPatternTags = (r: {
  co_count?: number | null;
  confidence?: number | null;
  lift?: number | null;
  support?: number | null;
}): string[] => {
  const tags: string[] = [];
  const co = r.co_count ?? 0;
  const conf = r.confidence ?? 0;
  const lift = r.lift ?? 0;
  const support = r.support ?? 0;

  // Volume perspective (co-purchases)
  if (co >= 10) tags.push("Stable pattern");
  else if (co >= 3) tags.push("Emerging pattern");
  else if (co > 0) tags.push("Low volume");

  // Conversion / follow-on perspective
  if (conf >= 0.5) tags.push("High follow-on");
  else if (conf >= 0.2) tags.push("Moderate follow-on");

  // Signal strength perspective (lift)
  if (lift >= 50) tags.push("Very strong signal");
  else if (lift >= 8) tags.push("Strong signal");
  else if (lift >= 4) tags.push("Medium signal");

  // Coverage perspective (support)
  if (support >= 0.05) {
    tags.push("Broad coverage");
  } else if (support >= 0.01) {
    tags.push("Focused coverage");
  }

  // Limit to max 3 tags so the UI does not get too crowded
  return tags.slice(0, 3);
};

// Tooltip label component used for metric labels.
const MetricLabel: React.FC<{ label: string; tooltip: string }> = ({
  label,
  tooltip,
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex cursor-help items-center gap-1">
        <span>{label}</span>
        <Info className="h-3 w-3 text-muted-foreground" />
      </span>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs text-[11px] leading-snug">
      {tooltip}
    </TooltipContent>
  </Tooltip>
);

export const CustomerProductRecommendations: React.FC<Props> = ({
  companyPartnerId,
}) => {
  const {
    data: recommendations,
    isLoading,
    error,
  } = useCustomerProductRecommendations({
    companyPartnerId,
    limit: 20,
    // Use backend filters to remove very weak/noisy rules.
    minLift: 1, // require at least neutral/positive lift
    minSupport: 0.005, // ~0.5% of orders as a minimal support
  });

  const [helpOpen, setHelpOpen] = useState<boolean>(false);

  const recoItemsRaw =
    recommendations?.recommendations.filter((r) => !!r.product_name) ?? [];

  // Sort recommendations by lift (desc), then by confidence (desc).
  const recoItems = [...recoItemsRaw].sort((a: any, b: any) => {
    const liftA = a.lift ?? 0;
    const liftB = b.lift ?? 0;
    if (liftB !== liftA) return liftB - liftA;

    const confA = a.confidence ?? 0;
    const confB = b.confidence ?? 0;
    return confB - confA;
  });

  if (!companyPartnerId) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-sm text-muted-foreground">
        Please select a company to see recommendations.
      </div>
    );
  }

  if (isLoading) {
    return (
      <section className="rounded-lg border bg-card p-4 shadow-sm">
        <div className="mb-2 h-4 w-40 animate-pulse rounded bg-muted" />
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} className="h-10 animate-pulse rounded bg-muted" />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-medium">Failed to load recommendations.</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <section className="rounded-lg border bg-card p-4 shadow-sm">
        {/* Header */}
        <div className="mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">
            Recommended products (cross-sell)
          </h2>
        </div>

        {/* How to read block (collapsible) */}
        <div className="mb-4 rounded-md border border-muted/50 bg-muted/30">
          <button
            type="button"
            className="flex w-full items-center justify-between px-3 py-2 text-[11px] text-muted-foreground"
            onClick={() => setHelpOpen((prev) => !prev)}
          >
            <span className="flex items-center gap-1 font-medium">
              <Info className="h-3 w-3" />
              <span>How to read these suggestions</span>
            </span>
            <span className="text-[10px] font-medium">
              {helpOpen ? "Hide details ▲" : "Show details ▼"}
            </span>
          </button>

          {helpOpen && (
            <div className="border-t border-muted/60 px-3 pb-3 pt-2 text-[11px] text-muted-foreground">
              {/* Scope / how the data is built */}
              <p className="mb-2">
                These suggestions are calculated from{" "}
                <b>historical, confirmed sales orders</b> in your company. For
                each order we look at the set of products bought together, then
                aggregate these patterns across all orders for this specific
                customer. The goal is to estimate{" "}
                <b>which additional products are most relevant to propose</b>{" "}
                based on what they already buy.
              </p>

              <p className="mb-2">
                Each card combines several metrics. Together they answer three
                practical questions:
              </p>
              <ul className="mb-2 ml-4 list-decimal space-y-1">
                <li>
                  <b>How often</b> do these products appear together?
                </li>
                <li>
                  <b>How likely</b> is the customer to buy this product when
                  they buy related items?
                </li>
                <li>
                  <b>How strong</b> is this effect compared with normal demand?
                </li>
              </ul>

              {/* Co-purchases */}
              <div className="mb-2">
                <p className="font-semibold">
                  1. Co-purchases — How often products appear together
                </p>
                <p>
                  <b>What is calculated?</b> Whenever a single order includes
                  both Product A and Product B, we count this as{" "}
                  <b>one co-purchase</b>. The system sums these counts across
                  all orders for this customer.
                </p>
                <p>
                  <b>Formula (for A and B):</b>{" "}
                  <code>co_count = number of orders containing both A and B</code>
                </p>
                <p>
                  <b>How to interpret:</b> A higher co-purchase count means the
                  pairing appears repeatedly, not just once by chance. Very low
                  values (1–2) should be treated as weak signals unless other
                  metrics (e.g. lift) are very strong.
                </p>
                <p>
                  <b>Business impact:</b> Use higher co-purchase counts as
                  evidence that the combination is familiar to customers. It is
                  usually safe to position these as “commonly bought together”
                  bundles or add-on suggestions.
                </p>
              </div>

              {/* Confidence */}
              <div className="mb-2">
                <p className="font-semibold">
                  2. Confidence (P(B | A)) — Follow-on likelihood
                </p>
                <p>
                  <b>What is calculated?</b> Among all orders that contain{" "}
                  <b>Product A</b> (often the items your customer already buys),
                  we look at how many also contain <b>Product B</b> (the
                  recommended product).
                </p>
                <p>
                  <b>Formula:</b>{" "}
                  <code>
                    confidence = co_count / product_a_order_count
                  </code>
                </p>
                <p>
                  <b>How to interpret:</b> Confidence is shown as a percentage
                  in the card:
                </p>
                <ul className="ml-4 list-disc">
                  <li>
                    50%+ → very strong follow-on: customers who buy A quite
                    often also buy B.
                  </li>
                  <li>
                    20–50% → moderate follow-on: worth training sales or adding
                    “You may also need…” suggestions.
                  </li>
                  <li>
                    5–20% → early signal: use with caution, maybe test in
                    campaigns or specific segments.
                  </li>
                </ul>
                <p>
                  <b>Business impact:</b> Confidence estimates the{" "}
                  <i>conversion potential</i> of your cross-sell pitch. A higher
                  value means that when the trigger product appears in a quote
                  or order, offering this item should result in more accepted
                  add-ons.
                </p>
              </div>

              {/* Support */}
              <div className="mb-2">
                <p className="font-semibold">
                  3. Support — Overall strength and stability of the pattern
                </p>
                <p>
                  <b>What is calculated?</b> Support looks at{" "}
                  <b>all orders</b> for this customer and asks: “In what share
                  of orders did A and B appear together?”
                </p>
                <p>
                  <b>Formula:</b>{" "}
                  <code>support = co_count / total_orders</code>
                </p>
                <p>
                  <b>How to interpret:</b>
                </p>
                <ul className="ml-4 list-disc">
                  <li>
                    &gt;= 5% → strong and broad pattern. The pairing is common in
                    day-to-day business.
                  </li>
                  <li>
                    1–5% → focused pattern. The signal is real but limited to a
                    subset of orders or use cases.
                  </li>
                  <li>
                    &lt; 1% → niche or very low volume. Use only when there is a
                    clear commercial context.
                  </li>
                </ul>
                <p>
                  <b>Business impact:</b> Support helps you judge whether a
                  pattern is <i>stable enough</i> to be used in standard
                  playbooks. High support suggests broader adoption (e.g. price
                  lists, bundles), while low support is more suited to
                  case-by-case proposals or pilots.
                </p>
              </div>

              {/* Lift */}
              <div className="mb-2">
                <p className="font-semibold">
                  4. Lift — Cross-sell strength compared to normal demand
                </p>
                <p>
                  <b>What is calculated?</b> Lift compares the probability of
                  buying B when A appears with the baseline probability of
                  buying B in any order.
                </p>
                <p>
                  <b>Formula:</b>{" "}
                  <code>
                    lift = confidence / (product_b_order_count / total_orders)
                  </code>
                </p>
                <p>
                  <b>How to interpret:</b>
                </p>
                <ul className="ml-4 list-disc">
                  <li>
                    Lift = 1 → buying A does not change the chance of buying B.
                  </li>
                  <li>
                    Lift = 3 → customers are 3× more likely to buy B when A is
                    present.
                  </li>
                  <li>
                    Lift = 10+ → very strong and specific cross-sell pattern.
                  </li>
                </ul>
                <p>
                  <b>Business impact:</b> Lift is the main ranking signal used
                  by the system. A high lift means B is{" "}
                  <i>disproportionately popular</i> when A appears, which usually
                  justifies training the sales team to consistently propose B
                  whenever they see A in a quote or order.
                </p>
                <p>
                  In practice, you can think of lift as a{" "}
                  <b>signal-to-noise ratio</b>: it helps distinguish “true cross-sell
                  relationships” from products that are just popular overall.
                </p>
              </div>

              {/* Trigger products */}
              <div className="mb-2">
                <p className="font-semibold">
                  5. Trigger products — Why this recommendation appears
                </p>
                <p>
                  <b>What is shown?</b> For each recommended product B, the
                  system lists one or more trigger products that the customer
                  already buys and that are statistically linked to B. In each
                  card you will see text like:
                </p>
                <p>
                  <code>Based on orders that include: A, C, D…</code>
                </p>
                <p>
                  <b>How to interpret:</b> These triggers point to{" "}
                  <i>concrete sales situations</i> where the recommendation is
                  relevant. For example, “When this customer buys A or C, they
                  often also buy B.”
                </p>
                <p>
                  <b>Business impact:</b> Use these trigger products to design
                  very specific actions:
                </p>
                <ul className="ml-4 list-disc">
                  <li>
                    Train sales reps: “If you see A in the order, always ask if
                    they also need B.”
                  </li>
                  <li>
                    Configure add-on suggestions in the quoting process for
                    these product combinations.
                  </li>
                  <li>
                    Build marketing campaigns that target customers currently
                    buying the trigger items but not yet buying the recommended
                    product.
                  </li>
                </ul>
              </div>

              {/* Putting it all together */}
              <p className="mt-2">
                <b>Putting it together:</b> a strong cross-sell candidate will
                usually have:
              </p>
              <ul className="ml-4 list-disc">
                <li>
                  <b>Enough co-purchases</b> to be confident the pattern is not
                  random,
                </li>
                <li>
                  <b>Good confidence</b> (high follow-on rate when trigger
                  products appear),
                </li>
                <li>
                  <b>Reasonable support</b> so the pattern is stable,
                </li>
                <li>
                  <b>High lift</b> indicating a clear uplift vs. normal demand.
                </li>
              </ul>
              <p className="mt-1">
                These metrics are not strict rules, but{" "}
                <b>decision helpers</b>. They show where the data suggests a
                strong cross-sell opportunity, so your team can focus their
                effort on the combinations with the highest impact.
              </p>
            </div>
          )}
        </div>

        {/* Empty case */}
        {recoItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No cross-sell recommendations are available for this customer yet.
          </p>
        ) : (
          // Six-column layout
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
            {recoItems.map((r: any, index: number) => {
              const liftCategory = getLiftCategory(r.lift);
              const tags = buildPatternTags(r);

              const triggerNames: string[] =
                (r.trigger_product_names ?? []).filter(
                  (x: string) => !!x && x.trim().length > 0
                );
              const triggerPreview = triggerNames.slice(0, 3).join(", ");
              const hasMoreTriggers =
                triggerNames.length > 3 ? "…" : "";

              // Prepare formatted description lines
              const reasonRaw = buildRecommendationReason(r);
              const reasonParts = reasonRaw
                .split(" • ")
                .map((part) => part.trim())
                .filter((part) => part.length > 0);

              const mainSentence = reasonParts[0] ?? "";
              const extraLines = reasonParts.slice(1);

              return (
                <div
                  key={r.product_id}
                  className="flex flex-col justify-between rounded-md border bg-muted/40 p-3"
                >
                  {/* Top row: rank + product + brand + lift strength badge */}
                  <div className="mb-2 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Priority badge based on sorted index */}
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                        #{index + 1}
                      </span>
                      <span className="text-xs font-semibold">
                        {r.product_name}
                      </span>
                      {r.brand_name && (
                        <span className="rounded bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {r.brand_name}
                        </span>
                      )}
                      {/* Lift strength badge */}
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${liftCategory.className}`}
                      >
                        {liftCategory.label}
                      </span>
                    </div>

                    {/* Pattern tags row */}
                    {tags.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1">
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Formatted description:
                        - first sentence bold
                        - extra parts as a small bullet list */}
                    <div className="text-[11px] text-muted-foreground">
                      {mainSentence && (
                        <p className="mb-0.5">
                          <span className="font-semibold">
                            {mainSentence}
                          </span>
                        </p>
                      )}
                      {extraLines.length > 0 && (
                        <ul className="ml-3 list-disc space-y-0.5">
                          {extraLines.map((line, idx) => (
                            <li key={idx}>{line}</li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Optional line explicitly listing trigger products */}
                    {triggerNames.length > 0 && (
                      <p className="text-[10px] text-muted-foreground">
                        <span className="font-semibold">
                          Based on orders that include:
                        </span>{" "}
                        <span className="font-medium">
                          {triggerPreview}
                          {hasMoreTriggers}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Metrics on a single line (wrapping if needed) */}
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
                    <span>
                      <MetricLabel
                        label="Co-purchases"
                        tooltip="Historical number of orders in your company where this product and the trigger items were bought together."
                      />{" "}
                      <span className="font-medium">{r.co_count}</span>
                    </span>
                    <span>
                      <MetricLabel
                        label="Support"
                        tooltip="Percentage of all orders that bought both this product and the trigger product(s). Higher support means the pattern is more broadly observed."
                      />{" "}
                      <span className="font-medium">
                        {formatPercent(r.support)}
                      </span>
                    </span>
                    <span>
                      <MetricLabel
                        label="Confidence"
                        tooltip="Among orders that include the trigger product(s), this is the percentage that also include this product (P(B | A)). Higher values mean the follow-on purchase is more common."
                      />{" "}
                      <span className="font-medium">
                        {formatPercent(r.confidence)}
                      </span>
                    </span>
                    <span>
                      <MetricLabel
                        label="Lift"
                        tooltip="Lift compares P(B | A) with the overall purchase rate of this product. A lift of 1 means no effect; a lift of 10 means the product is 10× more likely to be purchased when the trigger items appear; very high values (e.g. 100+) usually indicate a strong, specific cross-sell relationship."
                      />{" "}
                      <span className="font-medium">
                        {r.lift != null ? r.lift.toFixed(2) : "-"}
                      </span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </TooltipProvider>
  );
};
